import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SkillInput from './SkillInput';

// Players tab ⋯ (owner 2026-10-01).
//  Edit name — typos: same player, games / level / payment all stay.
//  Give spot — a different person takes the spot: the original keeps their
//  games and results (rankings keep them) but stops playing; the new player
//  signs up fresh and takes the queued games and the spot's payment.

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === 'string' ? d : null))) || fallback;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const C = {
  surface: '#0E0708', line: '#2E161B', lineStrong: '#3A1A20', amber: '#F59E0B',
  sheet: 'linear-gradient(180deg, #170B0E 0%, #100809 100%)',
};
const field = { width: '100%', minHeight: 44, background: '#140A0C', border: `1.5px solid ${C.lineStrong}`, borderRadius: 10,
  padding: '10px 12px', color: '#fff', fontSize: 15, outline: 'none' };

function Sheet({ testid, title, busy, onClose, children, footer }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" data-testid={testid}
      style={{ background: 'rgba(0,0,0,0.78)' }} onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true"
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-4 sm:p-5"
        style={{ background: C.sheet, border: `1px solid ${C.lineStrong}`,
                 boxShadow: '0 30px 80px #000, 0 0 0 1px rgba(230,57,70,0.12), 0 0 60px -20px rgba(230,57,70,0.35)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>{title}</h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        {children}
        <div className="flex gap-2 justify-end sticky bottom-0 pt-3 mt-3" style={{ background: '#100809' }}>{footer}</div>
      </div>
    </div>
  );
}

const btnCancel = { color: '#E5E5E5', border: `1px solid ${C.lineStrong}`, background: '#120A0C' };
const btnGo = (off) => ({ background: C.amber, color: '#111', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5,
  opacity: off ? 0.45 : 1, boxShadow: off ? 'none' : '0 6px 22px -6px rgba(245,158,11,0.75)' });

export function EditNameModal({ sessionId, player, requirePin, clearPin, onClose, onDone }) {
  const [name, setName] = useState(player.name || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const clean = name.split(/\s+/).filter(Boolean).join(' ');
  const off = busy || !clean || clean === player.name;
  const save = async () => {
    const pin = await requirePin();
    if (pin === null) return;
    setBusy(true);
    try {
      await sportsApi.put(`/sports/open-play/${sessionId}/players/${player.id}/name`, { name: clean, pin });
      toast.success(`Renamed ${player.name} → ${clean}`);
      onDone();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      setErr(errMsg(e, 'Could not rename'));
    } finally { setBusy(false); }
  };
  return (
    <Sheet testid="edit-name-modal" title="Edit name" busy={busy} onClose={onClose}
      footer={<>
        <button type="button" onClick={onClose} disabled={busy} className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-bold" style={btnCancel}>Cancel</button>
        <button type="button" data-testid="edit-name-save" onClick={save} disabled={off} className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-black uppercase" style={btnGo(off)}>
          {busy ? 'Saving…' : 'Save name'}
        </button>
      </>}>
      <p className="text-sm mt-1 mb-3" style={{ color: '#A3A3A3' }}>
        For typos and nicknames. Same player — games, level, payment and history all stay. The new name shows everywhere at once.
      </p>
      <label className="block text-xs font-bold mb-1.5 ml-0.5" style={{ color: '#BDBDBD' }}>Name</label>
      <input data-testid="edit-name-input" autoFocus value={name} maxLength={60} style={field}
        onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !off) save(); }} />
      <p className="text-[11.5px] mt-2" style={{ color: '#777' }}>Receipts already issued keep the old spelling.</p>
      {err && <p className="text-xs mt-3 px-3 py-2 rounded-lg" style={{ color: '#FCA5A5', background: '#EF44441A', border: '1px solid #EF444440' }}>{err}</p>}
    </Sheet>
  );
}

export function GiveSpotModal({ sessionId, player, queuedSeats = [], onCourtNow = null, requirePin, clearPin, onClose, onDone }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [skill, setSkill] = useState({ self_skill: 5, dupr: '' });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // On a court = refused; called but still waiting for a court (0-0) = the seat moves too.
  const onCourt = onCourtNow != null ? onCourtNow : player.status === 'playing';
  const paid = Number(player.amount_paid || 0) > 0;
  const owes = Math.max(0, Number(player.amount_due || 0) - Number(player.amount_paid || 0) - Number(player.waived_amount || 0));
  const ok = form.name.trim() && form.phone.trim();
  const newName = form.name.split(/\s+/).filter(Boolean).join(' ');

  const submit = async () => {
    const pin = await requirePin();
    if (pin === null) return;
    setBusy(true);
    try {
      const r = await sportsApi.post(`/sports/open-play/${sessionId}/players/${player.id}/give-spot`, {
        ...form, name: newName, self_skill: skill.self_skill, dupr: skill.dupr || undefined, pin,
      });
      toast.success(`${r.data?.new_player?.name} has ${player.name}'s spot${r.data?.queued_seats ? ` — ${plural(r.data.queued_seats, 'queued game', 'queued games')} moved` : ''}`);
      onDone();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      setErr(errMsg(e, 'Could not give the spot'));
      setConfirming(false);
    } finally { setBusy(false); }
  };

  const box = (title, items, accent) => (
    <div className="rounded-xl px-3 py-2.5" style={{ background: accent ? '#22C55E08' : C.surface, border: `1px solid ${accent ? '#22C55E44' : C.line}` }}>
      <p className="text-[10.5px] font-extrabold uppercase mb-1" style={{ color: accent ? '#4ADE80' : '#9A9A9A', letterSpacing: '0.12em' }}>{title}</p>
      <ul className="pl-4 list-disc text-[12.5px] leading-relaxed" style={{ color: '#D4D4D4' }}>{items.map(i => <li key={i}>{i}</li>)}</ul>
    </div>
  );

  return (
    <Sheet testid="give-spot-modal" title={confirming ? 'Give the spot?' : `Give ${player.name}'s spot`} busy={busy} onClose={onClose}
      footer={onCourt ? (
        <button type="button" onClick={onClose} className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-bold" style={btnCancel}>Close</button>
      ) : <>
        <button type="button" data-testid="give-cancel" onClick={() => (confirming ? setConfirming(false) : onClose())} disabled={busy}
          className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-bold" style={btnCancel}>{confirming ? 'Back' : 'Cancel'}</button>
        <button type="button" data-testid={confirming ? 'give-yes' : 'give-go'} disabled={busy || !ok}
          onClick={() => (confirming ? submit() : setConfirming(true))}
          className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-black uppercase" style={btnGo(busy || !ok)}>
          {busy ? 'Saving…' : confirming ? 'Yes, give spot' : 'Give spot'}
        </button>
      </>}>
      {confirming ? (
        <p className="text-sm mt-2" style={{ color: '#A3A3A3' }}>
          Are you sure? <b style={{ color: '#fff' }}>{player.name}</b> stops playing tonight
          {player.matches_played ? ` (their ${plural(player.matches_played, 'game stays', 'games stay')} on the record)` : ''} and{' '}
          <b style={{ color: '#fff' }}>{newName}</b> takes the spot{queuedSeats.length ? ` and their place in ${queuedSeats.join(', ')}` : ''}.
        </p>
      ) : (
        <>
          <p className="text-sm mt-1 mb-3" style={{ color: '#A3A3A3' }}>
            A different person takes this spot. {player.name} stays in the event with their record; the new player starts fresh.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {box(`${player.name} keeps`, [`${plural(player.matches_played || 0, 'game', 'games')} and their results`, 'Their place in the rankings', 'No more games or rating changes'])}
            {box('New player takes', [
              queuedSeats.length ? `${player.name}'s place in ${queuedSeats.join(', ')} — every match not played yet` : 'The next set picks them up',
              paid ? `Paid ₱${Number(player.amount_paid).toLocaleString()} (paid by ${player.name})` : owes > 0 ? `The unpaid fee — owes ₱${owes.toLocaleString()}` : 'The spot (no fee due)',
              'A fresh record — 0 games',
            ], true)}
          </div>
          {onCourt ? (
            <p className="text-xs mt-3 px-3 py-2.5 rounded-lg" style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' }}>
              <b>{player.name} is in a game right now.</b> Wait for that game to end, or use <b>Replace player</b> on the court first.
            </p>
          ) : (
            <>
              <p className="text-[10.5px] font-extrabold uppercase mt-4 mb-1" style={{ color: '#777', letterSpacing: '0.12em' }}>New player — same as signing up</p>
              <label className="block text-xs font-bold mt-2 mb-1.5 ml-0.5" style={{ color: '#BDBDBD' }}>Full name *</label>
              <input data-testid="give-name" style={field} value={form.name} placeholder="e.g. Kevin Santos" maxLength={60}
                onChange={e => setForm({ ...form, name: e.target.value })} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs font-bold mt-2 mb-1.5 ml-0.5" style={{ color: '#BDBDBD' }}>Phone *</label>
                  <input data-testid="give-phone" style={field} value={form.phone} placeholder="09xx xxx xxxx" inputMode="tel"
                    onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-bold mt-2 mb-1.5 ml-0.5" style={{ color: '#BDBDBD' }}>Email</label>
                  <input data-testid="give-email" style={field} value={form.email} placeholder="optional" inputMode="email"
                    onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="mt-3"><SkillInput value={skill} onChange={setSkill} sliderId="give-skill-slider" testid="give-skill" /></div>
            </>
          )}
        </>
      )}
      {err && <p data-testid="give-error" className="text-xs mt-3 px-3 py-2 rounded-lg" style={{ color: '#FCA5A5', background: '#EF44441A', border: '1px solid #EF444440' }}>{err}</p>}
    </Sheet>
  );
}
